import { AlertTriangle, Clock, Loader2 } from "lucide-react";

import { Modal } from "../Modal";
import { Button } from "./Button";

/**
 * Diálogo de confirmación (design system §6.8) — reemplazo de
 * `window.confirm` (prohibido por §6.8; hay ~10 usos en el admin).
 *
 * Muestra el **dato concreto** que se va a tocar en la descripción, y el
 * botón de acción usa verbo+objeto ("Anular boleta", no "Confirmar").
 * Componente controlado: el caller maneja `open` + `onConfirm`/`onCancel`
 * (típicamente con un `useState` local en la fila/pantalla).
 *
 * **Operaciones largas** (estimar cientos de lecturas, emitir un cierre,
 * generar un PDF grande): el caller deja el diálogo abierto mientras corre
 * (`loading`) y lo cierra al terminar. Con `loading` el diálogo no se cierra
 * con Esc, la X ni el fondo; `loadingLabel` reemplaza el texto del botón
 * (con spinner) y `loadingHint` explica cuánto puede tardar. Acompañar con
 * `useAvisoSalida(loading)` para avisar si se intenta cerrar la pestaña.
 */
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Cuerpo: qué se va a tocar y qué consecuencia tiene. */
  description?: React.ReactNode;
  /** Texto del botón de acción (verbo + objeto). Default "Confirmar". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` para acciones destructivas (rose). Default `danger`. */
  tone?: "danger" | "default";
  /** Deshabilita los botones mientras corre la mutación. */
  loading?: boolean;
  /** Texto del botón mientras corre (ej. "Estimando 817 lecturas…"). */
  loadingLabel?: string | undefined;
  /** Aviso bajo la descripción mientras corre (cuánto tarda, no cerrar). */
  loadingHint?: React.ReactNode | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  tone = "danger",
  loading = false,
  loadingLabel,
  loadingHint,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  // Mientras corre, ni Esc ni la X ni el fondo cierran: el diálogo es lo
  // único que le dice al operador que la operación sigue en curso.
  const cerrar = loading ? () => undefined : onCancel;
  return (
    <Modal
      open={open}
      onClose={cerrar}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "destructive" : "primary"}
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {loading && loadingLabel ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {loadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        {tone === "danger" && (
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-rose-50 text-rose-600">
            <AlertTriangle className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0 space-y-3">
          {description && <div className="text-sm text-neutral-600">{description}</div>}
          {loading && loadingHint && (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>{loadingHint}</span>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
